import get from 'lodash/get';

import type { VideoDescriptor } from '..';
import type { VideoDescriptorLayer } from '../layers';

import type { latest as LatestVDETypes } from '@libs/waymark-video/video-descriptor-types';
import {
  CustomEventTarget,
  matchObservableObjectChange,
  getObservableObjectKeyPath,
} from '@libs/util-ts';

export interface SceneSwitchOption {
  id: string;
  name: string;
  layers: VideoDescriptorLayer[];
}

export class SceneSwitch extends CustomEventTarget<{
  'change:selection': void;
}> {
  id: string;
  name: string;

  selectedOption: SceneSwitchOption;
  options: SceneSwitchOption[];

  frameNumber: number | null;

  videoDescriptor: VideoDescriptor;

  rawData: LatestVDETypes.SceneSwitch;

  constructor(sceneSwitch: LatestVDETypes.SceneSwitch, videoDescriptor: VideoDescriptor) {
    super();

    this.videoDescriptor = videoDescriptor;
    this.rawData = sceneSwitch;

    this.id = sceneSwitch.id;
    this.name = sceneSwitch.name;
    this.frameNumber = sceneSwitch.frameNumber;

    const switchGroupIDs = new Set(sceneSwitch.groups);
    const templateManifest = videoDescriptor.rawData.templateManifest;

    this.options = [];

    const initialSelectedOptionID = sceneSwitch.__activeValue ?? sceneSwitch.defaultSelection;
    let initialSelectedOption: SceneSwitchOption | null = null;

    for (const sceneGroup of templateManifest.sceneGroups ?? []) {
      if (!switchGroupIDs.has(sceneGroup.id)) {
        continue;
      }

      const option: SceneSwitchOption = {
        id: sceneGroup.id,
        name: sceneGroup.name,
        layers: sceneGroup.layers.map((layerID) => {
          const layer = videoDescriptor.findLayerByUUID(layerID);
          if (!layer) {
            throw new Error(`Layer with ID ${layerID} not found in video descriptor`);
          }

          return layer;
        }),
      };

      this.options.push(option);

      if (sceneGroup.id === initialSelectedOptionID) {
        initialSelectedOption = option;
      }
    }

    // Default to the first option if we didn't find a default selection
    if (!initialSelectedOption) {
      throw new Error(
        `Switch ${this.id} failed to find an initial selected option for __activeValue ${sceneSwitch.__activeValue} or defaultSelection ${sceneSwitch.defaultSelection}. This template may be misconfigured.`,
      );
    }

    this.selectedOption = initialSelectedOption;
    this.rawData.__activeValue = initialSelectedOption.id;

    const selectedLayerSet = new Set();

    // Make sure all layers in the selected option are visible
    for (const layer of initialSelectedOption.layers) {
      selectedLayerSet.add(layer);
      // Make sure all layers in the selected group are visible unless they were manually hidden by the user
      if (layer.getIsHidden()) {
        switch (layer.getHiddenBySource()) {
          case 'scene-switch-and-user-override':
            // If the layer was hidden by both the user and a scene switch,
            // keep it hidden but update the source to just "user-override"
            layer.setIsHidden(true, 'user-override');
            break;
          case 'scene-switch':
            // Show the layer if it was only hidden by a scene switch
            layer.setIsHidden(false, 'scene-switch');
            break;
          case 'user-override':
          default:
            // Leave the layer alone if it was manually hidden by the user
            break;
        }
      }
    }

    // Make sure all layers not in the selected option are hidden
    for (const option of this.options) {
      if (option === initialSelectedOption) {
        continue;
      }

      for (const layer of option.layers) {
        if (selectedLayerSet.has(layer)) {
          // Skip layers that are part of the selected option
          continue;
        }

        // Make sure non-selected option layers are hidden
        layer.setIsHidden(
          true,
          layer.getHiddenBySource() === 'user-override'
            ? 'scene-switch-and-user-override'
            : 'scene-switch',
        );
      }
    }

    this.videoDescriptor.addEventListener('change', (event) => {
      const isDataValid = this.revalidateRawData();
      if (!isDataValid) {
        return;
      }

      const switchDataKeyPath = getObservableObjectKeyPath(this.rawData);
      if (!switchDataKeyPath) {
        return;
      }

      const matchingSwitchActiveValueChange = matchObservableObjectChange(
        event.detail,
        `${switchDataKeyPath}.__activeValue`,
      );

      if (matchingSwitchActiveValueChange) {
        const [, prevValue, newValue] = matchingSwitchActiveValueChange;
        this.onSelectionChanged(prevValue, newValue);
      }
    });
  }

  /**
   * Checks that this.rawData is still pointing to the latest correct object in the video descriptor.
   * Disconnections can occur if the video descriptor is mass updated via something like VideoDescriptor.applyRawVideoDescriptorData()
   *
   * Returns true if the data is valid or has been reconnected, false if it has been disconnected and should be cleaned up/ignored.
   */
  private revalidateRawData(): boolean {
    const switchDataKeyPath = getObservableObjectKeyPath(this.rawData);
    if (!switchDataKeyPath) {
      return false;
    }

    let foundSwitchData = get(this.videoDescriptor.rawData, switchDataKeyPath);

    if (foundSwitchData === this.rawData) {
      return true;
    }

    if (foundSwitchData && foundSwitchData.id === this.rawData.id) {
      this.rawData = foundSwitchData;
      return true;
    }

    foundSwitchData = this.videoDescriptor.rawData.templateManifest.sceneSwitches?.find(
      (switchData) => switchData.id === this.rawData.id,
    );
    if (foundSwitchData) {
      this.rawData = foundSwitchData;
      return true;
    }

    console.error(`Data for content switch ${this.rawData.id} not found in video descriptor.`);
    return false;
  }

  /**
   * Updates layers and emits a change event when the switch's selected option changes.
   * We're doing this here instead of the setSelectedOption method so we can respond to external
   * video descriptor changes from things like undo/redo operations.
   */
  onSelectionChanged(previousSelectedOptionID: string, newSelectedOptionID: string) {
    if (previousSelectedOptionID === newSelectedOptionID) {
      // Bail out if the option ID didn't change
      return;
    }

    const previousSelectedOption = this.options.find(
      (option) => option.id === previousSelectedOptionID,
    );

    if (!previousSelectedOption) {
      throw new Error(`Switch option with ID ${previousSelectedOptionID} not found`);
    }

    const newSelectedOption = this.options.find((option) => option.id === newSelectedOptionID);

    if (!newSelectedOption) {
      throw new Error(`Switch option with ID ${newSelectedOptionID} not found`);
    }

    // Hide all layers for the previously selected option
    for (const layer of previousSelectedOption.layers) {
      layer.setIsHidden(
        true,
        layer.getHiddenBySource() === 'user-override'
          ? 'scene-switch-and-user-override'
          : 'scene-switch',
      );
    }
    // Show all layers for the new option
    for (const layer of newSelectedOption.layers) {
      if (layer.getIsHidden()) {
        switch (layer.getHiddenBySource()) {
          case 'scene-switch-and-user-override':
            // If the layer was hidden by both the user and a scene switch,
            // keep it hidden but update the source to just "user-override"
            layer.setIsHidden(true, 'user-override');
            break;
          case 'scene-switch':
            // Show the layer if it was only hidden by a scene switch
            layer.setIsHidden(false, 'scene-switch');
            break;
          case 'user-override':
          default:
            // Leave the layer alone if it was manually hidden by the user
            break;
        }
      }
    }
    this.selectedOption = newSelectedOption;

    this.dispatchEvent('change:selection', undefined);
  }

  getUUID() {
    return this.id;
  }

  getSelectedOption() {
    return this.selectedOption;
  }

  /**
   * Takes the ID for a switch option and sets it as the selected option.
   */
  setSelectedOption(optionID: string) {
    this.rawData.__activeValue = optionID;
  }

  getIdealDisplayFrame() {
    if (this.frameNumber !== null) {
      return this.frameNumber;
    }

    // If we didn't get an ideal frame number from the template manifest,
    // use the ideal display frame of the first layer in the selected option
    return this.selectedOption.layers[0].getIdealDisplayFrame();
  }

  /**
   * Get the ideal display time for this switch in seconds.
   */
  getIdealDisplayTime() {
    return this.getIdealDisplayFrame() / this.videoDescriptor.getFramerate();
  }

  getSelectedContentEditableLayers() {
    return this.selectedOption.layers.filter((layer) => layer.getCanEditFieldType('content'));
  }

  getAllContentEditableLayers() {
    const editableLayers = new Array<VideoDescriptorLayer>();
    for (const option of this.options) {
      for (const layer of option.layers) {
        if (layer.getCanEditFieldType('content')) {
          editableLayers.push(layer);
        }
      }
    }

    return editableLayers.sort((layerA, layerB) => {
      const displayTimeA = layerA.getIdealDisplayTime();
      const displayTimeB = layerB.getIdealDisplayTime();
      return displayTimeA - displayTimeB;
    });
  }
}
